import { Component, Show, createSignal, type JSX } from 'solid-js';
import { Preferences } from '@capacitor/preferences';
import { CRDR, NewAmount } from '../globals';

export { default as List } from './List';
export { default as MeasurementUnitsDropdown } from './MeasurementUnitsDropdown';
export * from './ToastProvider';
export { default as TaxComponent } from './Tax';
export { default as BreadCrumbsProvider, useBreadCrumbs } from './BreadCrumbsProvider';
export * as Icons from './Icons';
export { default as MasterCategorySelect, type MasterCategoryType, type MasterCategory } from './MasterCategorySelect';

export { default as Input } from './Input';
export { default as Select } from './Select';
export { twclsx } from './twclsx';
export { default as ThemeToggler } from './ThemeToggler';
export { default as ory, logout } from './ory';

// Define custom erro
export class EntityNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EntityNotFoundError';
  }
}

export type FetchOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: string | FormData;
  headers?: HeadersInit;
  credentials?: RequestCredentials;
};

let apiFetch: (url: string, options?: FetchOptions) => Promise<Response>;

const origin = import.meta.env.VITE_API_HOST;

if (['android', 'android_production'].includes(import.meta.env.MODE)) {
  const { value: sessionToken } = await Preferences.get({ key: 'hydrogen_session' });

  apiFetch = async (url: string, options?: FetchOptions) => {
    const headers = new Headers(options?.headers);
    headers.append('X-Session-Token', sessionToken!);

    return fetch(`${origin}/api${url}`, { ...options, credentials: 'include', headers });
  };
} else {
  apiFetch = (url: string, options?: FetchOptions) =>
    fetch(`${origin}/api${url}`, { ...options, credentials: 'include' });
}

export { apiFetch };

export const USDFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const dp = localStorage.getItem('decimal_precision');

export const [numberFormat, setNumberFormat] = createSignal(
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: dp ? Number.parseInt(dp, 10) : 2,
  }),
  { equals: false },
);

export const toCapitalCase = (str: string) =>
  str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const taxFormatter = (val: string | number) => Number.parseFloat(val.toString()) * 100;

export const amountFormatter = (val: number | undefined = 0): JSX.Element => (
  <>
    <Show when={val !== 0 && !Number.isNaN(val)}>
      <span class={val < 0 ? 'text-negative' : 'text-positive'}>
        {numberFormat().format(Math.abs(val))} {val < 0 ? 'CR' : 'DR'}
      </span>
    </Show>
  </>
);

export const newAmountFormatter: Component<NewAmount> = (val: NewAmount) => (
  <>
    <Show when={val && val.amount}>
      <span class={val.crdr === CRDR.CR ? 'text-negative' : 'text-positive'}>
        {numberFormat().format(val.amount || 0)} {val.crdr}
      </span>
    </Show>
  </>
);

export const idFormatter = (val: string) => val.slice(0, 8);

export const convertNumberToAmount = (val: number) => {
  const crdr = val < 0 ? CRDR.CR : CRDR.DR;
  return { amount: Math.abs(val), crdr };
};

export const convertAmountToNumber = (val: NewAmount): number =>
  val.crdr === CRDR.CR ? -(val.amount || 0) : val.amount || 0;

export const snakeCaseToString = (inputString: string) =>
  inputString
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const kebabCaseToString = (inputString: string) =>
  inputString
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const getThreeMonthsAgoStartOfMonth = () => {
  const today = new Date();
  const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
  const year = threeMonthsAgo.getFullYear();
  const month = (threeMonthsAgo.getMonth() + 1).toString().padStart(2, '0');
  const day = threeMonthsAgo.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};
